import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { createEvent } from '../../controllers/events';
import EventFormFields, { EVENT_TYPE_OPTIONS } from './EventFormFields';

export { EVENT_TYPE_OPTIONS } from './EventFormFields';

function toDateInputValue(date) {
  return new Date(date).toISOString().slice(0, 10);
}

function buildDefaultForm({ defaultCycleYear, fixedEventType, fixedVisibilityLevel, fixedRegionId, fixedDiocesisId, fixedComunidadId }) {
  const today = new Date();
  return {
    title: '',
    eventType: fixedEventType || EVENT_TYPE_OPTIONS[0],
    description: '',
    visibilityLevel: fixedVisibilityLevel || 4,
    regionId: fixedRegionId ? String(fixedRegionId) : '',
    diocesisId: fixedDiocesisId ? String(fixedDiocesisId) : '',
    comunidadId: fixedComunidadId ? String(fixedComunidadId) : '',
    locationName: '',
    locationAddress: '',
    modality: 'presencial',
    meetingUrl: '',
    cycleYear: defaultCycleYear || today.getFullYear(),
    startsOn: toDateInputValue(today),
    endsOn: toDateInputValue(today),
    sessionMode: 'single',
    singleSessionStartTime: '19:00',
    singleSessionEndTime: '21:00',
    registrationStatus: 'abierto',
    registrationCountMode: 'membership',
    capacityLimit: '',
    paymentType: 'free',
    feeAmount: '',
    currency: 'USD',
    certificateTemplate: fixedEventType || 'default',
    sessions: [],
  };
}

function normalizePaymentTypeForVisibility(paymentType, visibilityLevel) {
  if (parseInt(visibilityLevel) !== 1 && paymentType === 'stripe') {
    return 'offline';
  }

  return paymentType;
}

function EventCreateModal({
  show,
  onHide,
  token,
  org,
  defaultCycleYear,
  fixedEventType,
  fixedVisibilityLevel,
  fixedRegionId,
  fixedDiocesisId,
  fixedComunidadId,
  modalTitle = 'Crear evento',
  onCreated,
}) {
  const [form, setForm] = useState(buildDefaultForm({
    defaultCycleYear,
    fixedEventType,
    fixedVisibilityLevel,
    fixedRegionId,
    fixedDiocesisId,
    fixedComunidadId,
  }));
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (!show) {
      return;
    }
    setForm(buildDefaultForm({
      defaultCycleYear,
      fixedEventType,
      fixedVisibilityLevel,
      fixedRegionId,
      fixedDiocesisId,
      fixedComunidadId,
    }));
    setCreateError('');
  }, [show, defaultCycleYear, fixedEventType, fixedVisibilityLevel, fixedRegionId, fixedDiocesisId, fixedComunidadId]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateVisibilityLevel(levelValue) {
    const level = parseInt(levelValue);
    setForm((prev) => {
      let nextRegionId = prev.regionId;
      let nextDiocesisId = prev.diocesisId;
      let nextComunidadId = prev.comunidadId;
      const nextPaymentType = normalizePaymentTypeForVisibility(prev.paymentType, level);

      if (level < 2) {
        nextRegionId = '';
        nextDiocesisId = '';
        nextComunidadId = '';
      }
      else if (level < 3) {
        nextDiocesisId = '';
        nextComunidadId = '';
      }
      else if (level < 4) {
        nextComunidadId = '';
      }

      return {
        ...prev,
        visibilityLevel: level,
        regionId: nextRegionId,
        diocesisId: nextDiocesisId,
        comunidadId: nextComunidadId,
        paymentType: nextPaymentType,
      };
    });
  }

  function updateRegion(regionId) {
    setForm((prev) => ({
      ...prev,
      regionId,
      diocesisId: '',
      comunidadId: '',
    }));
  }

  function updateDiocesis(diocesisId) {
    setForm((prev) => ({
      ...prev,
      diocesisId,
      comunidadId: '',
    }));
  }

  function updateSession(index, key, value) {
    setForm((prev) => {
      const sessions = [...prev.sessions];
      sessions[index] = { ...sessions[index], [key]: value };
      return { ...prev, sessions };
    });
  }

  function updateSessionMode(mode) {
    setForm((prev) => {
      if (mode === 'single') {
        return { ...prev, sessionMode: mode, sessions: [] };
      }
      if (!prev.sessions.length) {
        return {
          ...prev,
          sessionMode: mode,
          sessions: [{
            sessionDate: prev.startsOn,
            startTime: '19:00',
            endTime: '21:00',
            locationName: '',
            locationAddress: '',
            meetingUrl: '',
          }],
        };
      }
      return { ...prev, sessionMode: mode };
    });
  }

  function addSession() {
    if (form.sessionMode === 'single') {
      return;
    }
    setForm((prev) => ({
      ...prev,
      sessions: [...prev.sessions, {
        sessionDate: prev.endsOn,
        startTime: '19:00',
        endTime: '21:00',
        locationName: '',
        locationAddress: '',
        meetingUrl: '',
      }],
    }));
  }

  function removeSession(index) {
    setForm((prev) => {
      if (prev.sessions.length <= 1) {
        return prev;
      }
      return { ...prev, sessions: prev.sessions.filter((_, i) => i !== index) };
    });
  }

  async function handleCreateEvent(e) {
    e.preventDefault();
    setSaving(true);
    setCreateError('');

    try {
      const effectiveVisibilityLevel = fixedVisibilityLevel || form.visibilityLevel;
      const effectiveRegionId = fixedRegionId || form.regionId;
      const effectiveDiocesisId = fixedDiocesisId || form.diocesisId;
      const effectiveComunidadId = fixedComunidadId || form.comunidadId;
      const effectivePaymentType = normalizePaymentTypeForVisibility(form.paymentType, effectiveVisibilityLevel);

      const payload = {
        ...form,
        eventType: fixedEventType || form.eventType,
        visibilityLevel: parseInt(effectiveVisibilityLevel),
        regionId: effectiveRegionId ? parseInt(effectiveRegionId) : undefined,
        diocesisId: effectiveDiocesisId ? parseInt(effectiveDiocesisId) : undefined,
        comunidadId: effectiveComunidadId ? parseInt(effectiveComunidadId) : undefined,
        cycleYear: parseInt(form.cycleYear),
        capacityLimit: form.capacityLimit ? parseInt(form.capacityLimit) : undefined,
        paymentType: effectivePaymentType,
        feeAmount: effectivePaymentType === 'free' ? 0 : (form.feeAmount ? parseFloat(form.feeAmount) : undefined),
        singleSessionStartTime: form.sessionMode === 'single' ? form.singleSessionStartTime : undefined,
        singleSessionEndTime: form.sessionMode === 'single' ? form.singleSessionEndTime : undefined,
        certificateTemplate: fixedEventType || form.certificateTemplate,
        sessions: form.sessionMode === 'single' ? [{
          sessionDate: form.startsOn,
          startTime: form.singleSessionStartTime,
          endTime: form.singleSessionEndTime,
          meetingUrl: form.meetingUrl || '',
          locationName: form.locationName || '',
          locationAddress: form.locationAddress || '',
        }] : form.sessions,
      };

      const created = await createEvent(token, payload);
      if (onCreated) {
        await onCreated(created);
      }
      onHide();
    }
    catch (e2) {
      setCreateError(e2.message || 'No se pudo crear el evento.');
    }
    finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Form onSubmit={handleCreateEvent}>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!createError && <Alert variant="danger" onClose={() => setCreateError('')} dismissible>{createError}</Alert>}
          <EventFormFields
            form={form}
            org={org}
            fixedEventType={fixedEventType}
            fixedVisibilityLevel={fixedVisibilityLevel}
            fixedRegionId={fixedRegionId}
            fixedDiocesisId={fixedDiocesisId}
            fixedComunidadId={fixedComunidadId}
            onFieldChange={updateField}
            onVisibilityLevelChange={updateVisibilityLevel}
            onRegionChange={updateRegion}
            onDiocesisChange={updateDiocesis}
            onSessionModeChange={updateSessionMode}
            onAddSession={addSession}
            onRemoveSession={removeSession}
            onSessionChange={updateSession}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancelar</Button>
          <Button type="submit" disabled={saving}>
            {saving ? <><Spinner size="sm" /> Guardando...</> : 'Crear evento'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default EventCreateModal;
